import { FieldTypeCategory, isParameterTyped, simplifyType } from '@thinkalpha/language-services';
import type { DataType } from '@thinkalpha/language-services';
import type { OutputFilter } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnFilter, ColumnFilterMatch, ColumnFilters } from '@thinkalpha/ui-table/interactions/columnFilters';
import { escapeFormulaStringLiteral } from 'src/lib/escapeFormulaStringLiteral';
import { combineTableFilters } from 'src/models/TableWidgetModel/TableFilterModel';

const STRING_MATCHES: readonly ColumnFilterMatch[] = ['contains', 'equals'];
const NO_MATCHES: readonly ColumnFilterMatch[] = [];

/**
 * The matches a table formula can apply to a column of this type (ADR 0014, AC-FILTER-007): string
 * columns compile `contains` to `like` and `equals` to `str_cmp`. Empty means the column
 * cannot filter; other types need their own operators first.
 */
export function tableFormulaFilterMatches(dataType: DataType | null | undefined): readonly ColumnFilterMatch[] {
    return dataType != null && !isParameterTyped(dataType) && simplifyType(dataType) === FieldTypeCategory.String
        ? STRING_MATCHES
        : NO_MATCHES;
}

/** A column's filter capability: its header label and the matches its table can apply. */
export interface FilterableColumn {
    readonly label: string;
    readonly matches: readonly ColumnFilterMatch[];
}

/**
 * Template columns whose table formula can filter them, hidden ones included, in template order.
 * Client columns are computed in the browser, so the server cannot filter them.
 */
export function filterableTemplateColumns(
    columns: readonly { id: string; name: string; type: string; dataType?: DataType | null }[],
): ReadonlyMap<string, FilterableColumn> {
    return new Map(
        columns.flatMap((column) => {
            const matches = column.type === 'client' ? NO_MATCHES : tableFormulaFilterMatches(column.dataType);
            return matches.length > 0 ? [[column.id, { label: column.name, matches }] as const] : [];
        }),
    );
}

const REGEX_METACHARACTERS = /[.*+?^${}()|[\]\\]/g;

/** Escapes a value for a regex `like` pattern, then for the formula string literal around it. */
function likePattern(value: string): string {
    return escapeFormulaStringLiteral(value.replace(REGEX_METACHARACTERS, '\\$&'));
}

// Table formula string literals are typed by prefix: `$` for a string, `%` for a regex.
function termClause(columnId: string, { mode, value, exact }: ColumnFilter): string {
    const trimmed = value.trim();
    if (exact) {
        const operator = mode === 'include' ? '==' : '!=';
        return `(str_cmp(\`${columnId}\`, '$${escapeFormulaStringLiteral(trimmed)}') ${operator} 0)`;
    }
    const contains = `(\`${columnId}\` like '%.*${likePattern(trimmed)}.*')`;
    // OD-FILTER-003: the engine's negation syntax is unconfirmed.
    return mode === 'include' ? contains : `(not ${contains})`;
}

/**
 * Compiles column filters to a table-server `raw-formula` clause, or undefined when nothing applies.
 * Includes within a column are ORed, excludes are ANDed, and columns are ANDed. Terms on columns
 * `isFilterable` rejects are skipped, so a column that leaves the table or loses eligibility stops
 * filtering without any state cleanup.
 */
export function columnFiltersToTableFormula(
    filters: ColumnFilters<string>,
    isFilterable: (columnId: string) => boolean,
): string | undefined {
    const clauses = Object.entries<ColumnFilter[] | undefined>(filters).flatMap(([columnId, terms = []]) => {
        if (!isFilterable(columnId)) return [];
        const active = terms.filter((term) => term.value.trim().length > 0);
        const includes = active.filter((term) => term.mode === 'include').map((term) => termClause(columnId, term));
        const excludes = active.filter((term) => term.mode === 'exclude').map((term) => termClause(columnId, term));
        const anyInclude = includes.length > 1 ? [`(${includes.join(' or ')})`] : includes;
        return [...anyInclude, ...excludes];
    });
    return clauses.length > 0 ? clauses.join(' and ') : undefined;
}

/**
 * A plain-text search as a `raw-formula` clause over `columnIds`, matching the table server's
 * `search` filter (a substring match on any string column). A table query carries one filter, so
 * widgets that search send this instead while column filters are active.
 */
export function searchToTableFormula(text: string, columnIds: Iterable<string>): string | undefined {
    const trimmed = text.trim();
    if (!trimmed) return undefined;
    const clauses = Array.from(columnIds, (columnId) => termClause(columnId, { mode: 'include', value: trimmed }));
    return clauses.length > 0 ? clauses.join(' or ') : '(1 == 0)';
}

/**
 * The output filter for a table that searches, with column filters ANDed in. A table query carries
 * one filter, so while column filters apply the search is sent as an equivalent formula over
 * `searchColumnIds`; otherwise the filter is the plain search, exactly as before column filters.
 */
export function searchWithColumnFilters(
    search: string | null | undefined,
    columnFormula: string | undefined,
    searchColumnIds: Iterable<string>,
): OutputFilter | undefined {
    if (columnFormula === undefined) {
        return search ? { type: 'search', value: search } : undefined;
    }
    const searchFormula = search ? searchToTableFormula(search, searchColumnIds) : undefined;
    return { type: 'raw-formula', value: combineTableFilters(searchFormula, columnFormula) ?? columnFormula };
}
